import { useGetFulfilmentCentersQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { skipToken } from '@reduxjs/toolkit/query'
import { format } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import CountCard from '../../components/cards/CountCard'
import { Status } from '../../components/cards/statusTag'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import { SearchInput } from '../../components/inputs/searchInput'
import { ExportDataModal } from '../../components/modal/app_modals/exportDataModal'
import { FilterOrdersModal } from '../../components/modal/app_modals/filterOrdersModal'
import { TableComponent } from '../../components/table'
import { PageHeader } from '../../components/typography/PageHeader'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import {
  NUMBER_OF_ITEMS_PER_PAGE,
  OrderChannelMap,
  ORDERSTATUS,
} from '../../constants'
import { formatCurrency } from '../../helpers'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { useModalControl } from '../../hooks/useModalControl'
import { usePermissions } from '../../hooks/usePermissions'
import Layout from '../../layout'
import {
  orderSelector,
  updateNewOrderStatus,
} from '../../redux-toolkits/orders/order.reducer'
import {
  orderApiSlice,
  useGetHarmonizedOrdersQuery,
  useGetOrdersMetricsCountQuery,
  useGetOrdersMetricsValueQuery,
} from '../../redux-toolkits/orders/orders.slice'
import {
  OrderChannelStatus,
  OrderQueryProps,
} from '../../redux-toolkits/orders/orders.type'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import { StatusTone } from '../../redux-toolkits/shared/shared.type'
export const OrdersPage: React.FC = () => {
  const [query, setQuery] = useState<OrderQueryProps>()

  const { selectedFulfilmentCenters, user } = useAppSelector(
    (state) => state.auth,
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const { data: centres } = useGetFulfilmentCentersQuery({})
  const centreFilter = selectedFulfilmentCenters
    .map((centre) => centre._id)
    .join(',')
  const filters = query
    ? {
        startDate: query.startDate,
        endDate: query.endDate,
        fulfilmentCentre: centreFilter,
        vendorId: query.vendorId,
        creatorType: query.creatorType,
        isCustomDateRange: query.isCustomDateRange,
        filter: query.filter,
        status: query.status,
        channel: query.channel,
        search: query.search,
      }
    : undefined
  const previousCentreFilter = useRef(centreFilter)
  useEffect(() => {
    if (previousCentreFilter.current === centreFilter) return
    previousCentreFilter.current = centreFilter
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('page', '1')
        return next
      },
      { replace: true },
    )
  }, [centreFilter])

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { isNewOrderReceived } = useAppSelector(orderSelector)

  const dispatch = useAppDispatch()

  const { hasPermissions } = usePermissions()

  const { data: counts, isFetching: fetchingCountsMetrics } =
    useGetOrdersMetricsCountQuery(filters ?? skipToken, {
      refetchOnMountOrArgChange: true,
    })

  const { data: values, isFetching: fetchingValuesMetrics } =
    useGetOrdersMetricsValueQuery(
      filters && hasPermissions(['view_revenue_metrics']) ? filters : skipToken,
      { refetchOnMountOrArgChange: true },
    )

  const {
    data: payload,
    isFetching,
    isSuccess,
    error: ordersError,
  } = useGetHarmonizedOrdersQuery(
    query
      ? {
          ...query,
          ...filters,
          paymentStatus: 'PAID',
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  )

  useEffect(() => {
    if (!isFetching && isSuccess) {
      dispatch(updateNewOrderStatus(false))
    }
  }, [isFetching, isSuccess])

  useEffect(() => {
    if (isNewOrderReceived) {
      dispatch(
        orderApiSlice.util.invalidateTags([
          { type: 'ORDER', id: 'LIST' },
          'ORDER_METRICS',
        ]),
      )
    }
  }, [isNewOrderReceived])

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: NUMBER_OF_ITEMS_PER_PAGE,
      status: searchParams.get('status') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      search: searchParams.get('search') || undefined,
      channel: (searchParams.get('channel') as OrderChannelStatus) || undefined,
      vendorId: searchParams.get('vendorId') || undefined,
      creatorType:
        (searchParams.get('creatorType') as OrderQueryProps['creatorType']) ||
        undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="mb-10 flex items-center justify-between gap-4">
        <PageHeader title={`Orders`} className="capitalize !mb-0" />

        <DateFilterNav
          filter={query?.filter ?? 'custom'}
          isCustomDateRange={query?.isCustomDateRange ?? false}
          startDate={query?.startDate}
          endDate={query?.endDate}
          setFilter={(filter) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              filter: filter.filter ?? '',
              isCustomDateRange: String(filter.isCustomDateRange),
              endDate: filter.endDate ?? '',
              startDate: filter.startDate ?? '',
            })
          }}
        />
      </div>

      <CountCardContainer>
        <CountCard
          count={
            hasPermissions(['view_revenue_metrics'])
              ? values?.data?.totalOrderValue ?? 0
              : 0
          }
          text="Total order value"
          isCurrency={true}
          loading={fetchingValuesMetrics}
        />
        <CountCard
          count={
            hasPermissions(['view_revenue_metrics'])
              ? values?.data?.processingOrderValue ?? 0
              : 0
          }
          text="Processing order value"
          isCurrency={true}
          loading={fetchingValuesMetrics}
        />
        <CountCard
          count={
            hasPermissions(['view_revenue_metrics'])
              ? values?.data?.completedOrderValue ?? 0
              : 0
          }
          text="Completed order value"
          isCurrency={true}
          loading={fetchingValuesMetrics}
        />
        {/* <CountCard
              count={value?.pendingOrderValue ?? 0}
              text="Pending order"
              icon=""
              isCurrency={true}
            /> */}
        {/* <CountCard
              count={value?.cancelledOrderValue ?? 0}
              text="Cancelled order"
              icon=""
              isCurrency={true}
            /> */}
        <CountCard
          count={counts?.data?.totalOrderCount ?? 0}
          text="Total fulfilment orders"
          isCurrency={false}
          loading={fetchingCountsMetrics}
        />
        <CountCard
          count={counts?.data?.processingOrderCount ?? 0}
          text="Processing fulfilment orders"
          isCurrency={false}
          loading={fetchingCountsMetrics}
        />
        <CountCard
          count={counts?.data?.completedOrderCount ?? 0}
          text="Completed fulfilment orders"
          isCurrency={false}
          loading={fetchingCountsMetrics}
        />
        {/* <CountCard
              count={count?.pendingOrderCount ?? 0}
              text="Pending order count"
              icon=""
              isCurrency={false}
            /> */}
        {/* <CountCard
              count={count?.cancelledOrderCount ?? 0}
              text="Cancelled order count"
              icon=""
              isCurrency={false}
            /> */}
      </CountCardContainer>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="!w-full flex-1">
          <SearchInput
            searchFunction={(search) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                page:
                  search !== searchParams.get('search')
                    ? '1'
                    : searchParams.get('page') ?? '1',
                search: search,
              })
            }}
            placeholder="Search reference"
            initialValue={searchParams.get('search') ?? ''}
          />
        </div>

        <Button
          label={`Filters`}
          primary={false}
          icon="filter"
          className="!font-normal"
          onClick={() => {
            handleModalOpen('filterModal')
          }}
        />

        <Button
          label="Export"
          onClick={async () => {
            handleModalOpen('exportDataModal')
          }}
          loading={false}
          primary
          className="w-full max-w-[129px]"
        />
      </div>

      <p className="text-sm text-sec-black mb-3">
        Values and counts cover the selected centres. Each row groups their
        fulfilment orders under one checkout reference.
      </p>
      {ordersError && (
        <p className="text-danger mb-3">
          Unable to load orders. Check that your account is assigned to the
          selected physical centres.
        </p>
      )}
      <div className="w-full overflow-x-scroll">
        <TableComponent
          name={''}
          showName={false}
          headers={[
            'Harmonized Reference',
            'Order value',
            'Fulfilment centres',
            'Type',
            'Channel',
            'Date',
            'Status',
          ]}
          rows={(payload?.data?.orders ?? []).map((order) => {
            return {
              id: order.harmonizedReference,
              content: [
                <div key={`${order.harmonizedReference}-reference`}>
                  <Link
                    to={`/orders/${order.harmonizedReference}`}
                    className="
                      hover:text-black hover:underline
                      transition-all duration-500 font-recoleta w-fit
                      "
                  >
                    #{order.harmonizedReference}
                  </Link>

                  <p className="text-xs">{order.completionSummary}</p>
                </div>,
                `${formatCurrency(order.totalAmount)}`,
                order.fulfilmentCentres
                  ?.map(
                    (id) =>
                      centres?.data.docs.find((centre) => centre._id === id)
                        ?.name ??
                      user?.fulfillmentCenter?.find(
                        (centre) => centre._id === id,
                      )?.name ??
                      id,
                  )
                  .join(', ') ?? '—',
                order.deliveryType === 'DELIVERY' ? 'Delivery' : 'Pickup',
                OrderChannelMap[order.channel]?.text ?? 'N/A',
                `${format(order.createdAt, 'dd/MM/yyyy h:mma')}`,
                <div
                  key={`${order.harmonizedReference}-status`}
                  className="flex items-center gap-2 overflow-visible"
                >
                  <Status
                    text={order?.orderStatus
                      ?.toLowerCase()
                      ?.toLowerCase()
                      ?.split('_')
                      ?.join(' ')}
                    type={
                      (ORDERSTATUS.find(
                        (status) =>
                          status.name.toLowerCase() ===
                          order.orderStatus.toLowerCase(),
                      )?.type ?? 'warn') as StatusTone
                    }
                  />
                </div>,
              ],
            }
          })}
          isEmpty={[undefined, 0].includes(payload?.data?.orders?.length)}
          loading={isFetching}
          currentPage={Number(searchParams.get('page') ?? 1)}
          totalDataCount={payload?.data?.total ?? 0}
          pageLimit={NUMBER_OF_ITEMS_PER_PAGE}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              page: String(page),
            })
          }}
        />
      </div>

      <FilterOrdersModal
        modalOpen={modalOpen && modal === 'filterModal'}
        handleModalClose={() => {
          handleModalClose()
        }}
        setModalOpen={setModalOpen}
        query={query}
        onSucess={(q) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            page: String(q.page ?? 1),
            channel: q.channel || '',
            status: q.status || '',
            vendorId: q.vendorId || '',
            creatorType: q.creatorType || '',
          })
        }}
      />

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        fulfilmentCentre={centreFilter}
        extraValues={{
          startDate: query?.startDate,
          endDate: query?.endDate,
          isCustomDateRange: query?.isCustomDateRange,
          filter: query?.filter,
          search: query?.search,
          orderStatus: query?.status,
          channel: query?.channel,
          creatorType: query?.creatorType,
          creatorIds: query?.vendorId
            ? JSON.stringify(query.vendorId.split(',').filter(Boolean))
            : undefined,
        }}
        title={`Export Orders`}
        reportType="daily_order_summary"
      />
    </Layout>
  )
}
